<script lang="ts">
	import { twMerge } from 'tailwind-merge';
	import { Tooltip } from '../Tooltip';
	import type { RangeSliderValue } from './types';

	let {
		min,
		max,
		value = $bindable([min, max]),
		step = 1,
		onChange = undefined,
		dataCy,
		...rest
	}: Props = $props();

	interface Props {
		min: number;
		max: number;
		value?: RangeSliderValue;
		step?: number;
		onChange?: ((value: RangeSliderValue) => void) | undefined;
		dataCy: string;
		class?: string;
	}

	let dataCyPrefix = $derived(`${dataCy}-range-slider`);
	let draggingIndex = $state<number | null>(null);
	let activeIndex = $state<number | null>(null);
	let trackEl: HTMLSpanElement | undefined = $state();
	let thumbEls: HTMLSpanElement[] = $state([]);

	function toPercent(val: number) {
		return ((val - min) / (max - min)) * 100;
	}

	function clampAndSnap(raw: number): number {
		const snapped = Math.round(raw / step) * step;
		return Math.min(max, Math.max(min, snapped));
	}

	function getValueFromPosition(clientX: number): number {
		if (!trackEl) return min;
		const rect = trackEl.getBoundingClientRect();
		const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
		return clampAndSnap(min + ratio * (max - min));
	}

	function findNearestThumbIndex(targetValue: number): number {
		let nearestIndex = 0;
		let nearestDist = Math.abs(value[0] - targetValue);
		for (let i = 1; i < value.length; i++) {
			const dist = Math.abs(value[i] - targetValue);
			if (dist < nearestDist) {
				nearestDist = dist;
				nearestIndex = i;
			}
		}
		return nearestIndex;
	}

	function focusThumb(index: number) {
		activeIndex = index;
		thumbEls[index]?.focus();
	}

	function updateValue(index: number, newVal: number) {
		const next = [...value];
		next[index] = newVal;

		// Prevent thumbs from crossing each other
		if (index > 0 && next[index] < next[index - 1]) {
			next[index] = next[index - 1];
		}
		if (index < next.length - 1 && next[index] > next[index + 1]) {
			next[index] = next[index + 1];
		}

		if (JSON.stringify(value) !== JSON.stringify(next)) {
			value = next;
			onChange?.(next);
		}
	}

	function handleTrackPointerDown(e: PointerEvent) {
		if (e.button !== 0) return;
		e.preventDefault();
		const targetValue = getValueFromPosition(e.clientX);
		const index = findNearestThumbIndex(targetValue);
		updateValue(index, targetValue);
		draggingIndex = index;
		focusThumb(index);
		trackEl?.setPointerCapture(e.pointerId);
	}

	function handleThumbPointerDown(index: number) {
		return (e: PointerEvent) => {
			if (e.button !== 0) return;
			e.preventDefault();
			e.stopPropagation();
			draggingIndex = index;
			focusThumb(index);
			trackEl?.setPointerCapture(e.pointerId);
		};
	}

	function handlePointerMove(e: PointerEvent) {
		if (draggingIndex === null) return;
		updateValue(draggingIndex, getValueFromPosition(e.clientX));
	}

	function handlePointerUp() {
		draggingIndex = null;
	}

	function handleKeyDown(e: KeyboardEvent) {
		if (activeIndex === null) return;
		const index = activeIndex;
		let newVal: number;
		switch (e.key) {
			case 'ArrowRight':
			case 'ArrowUp':
				newVal = Math.min(max, value[index] + step);
				break;
			case 'ArrowLeft':
			case 'ArrowDown':
				newVal = Math.max(min, value[index] - step);
				break;
			case 'Home':
				newVal = min;
				break;
			case 'End':
				newVal = max;
				break;
			default:
				return;
		}
		e.preventDefault();
		updateValue(index, newVal);
	}
</script>

<span class={twMerge('flex w-full justify-center', rest.class)} data-cy={dataCyPrefix}
	><span
		bind:this={trackEl}
		class="relative flex h-[18px] w-[calc(100%-12px)] items-center hover:cursor-pointer"
		role="group"
		onpointerdown={handleTrackPointerDown}
		onpointermove={handlePointerMove}
		onpointerup={handlePointerUp}
	>
		<span class="block h-[4px] w-full rounded-sm bg-gray-200">
			<span
				class="range-slider-track h-[4px] block absolute"
				style="left: {toPercent(value[0])}%; right: {100 - toPercent(value[value.length - 1])}%;"
				data-cy={`${dataCyPrefix}-track`}
			></span>
		</span>

		{#each value as thumbVal, i}
			<span
				bind:this={thumbEls[i]}
				role="slider"
				tabindex={0}
				aria-valuemin={min}
				aria-valuemax={max}
				aria-valuenow={thumbVal}
				class="absolute flex h-[40px] w-[40px] items-center justify-center outline-none"
				style="left: calc({toPercent(thumbVal)}% - 20px);"
				data-cy={`${dataCyPrefix}-thumb-${i}`}
				onpointerdown={handleThumbPointerDown(i)}
				onfocus={() => (activeIndex = i)}
				onkeydown={handleKeyDown}
			>
				<Tooltip
					dataCy="test"
					content={`${thumbVal}`}
					forceOpen={draggingIndex === i}
					tabIndex={-1}
				>
					<span
						class="bg-gray-0 block h-[18px] w-[18px] rounded-full border border-gray-500 focus:ring-4 focus:ring-black/40"
					></span>
				</Tooltip>
			</span>
		{/each}
	</span>
</span>

<style>
	.range-slider-track {
		background-color: var(--sliderTrackColor, var(--color-gray-900));
	}
</style>
